import { useScrollReveal } from '@/hooks/useScrollReveal';

interface ProjectOverviewProps {
  project: {
    description: string;
    paymentPlan?: string;
  };
}

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;
const PHONE_URL = `tel:${WHATSAPP_NUMBER}`;

export default function ProjectOverview({ project }: ProjectOverviewProps) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section ref={ref} className="w-full py-16 md:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 lg:gap-16">
          {/* Left: Editorial Content */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-4">
              Project Overview
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight mb-6">
              A Vision for Modern Living in{' '}
              <span className="text-[#d4853a]">Islamabad&apos;s Prime Zone</span>
            </h2>

            <div className="space-y-5 text-[15px] text-charcoal-50 leading-[1.8]">
              <p>{project.description}</p>
              <p>
                Strategically positioned in Islamabad&apos;s rapidly developing E-19 sector, this gated community
                combines the serenity of nature with the convenience of urban connectivity. Every plot is designed
                to maximize value appreciation while offering a lifestyle of comfort and prestige.
              </p>
              <p>
                With a proven track record of delivering verified, NOC-approved projects, Sunshine Real Estate
                ensures that your investment is backed by transparency, legal clarity, and expert guidance
                from booking to possession and beyond.
              </p>
            </div>

            {project.paymentPlan && (
              <div className="mt-8 p-5 md:p-6 bg-white rounded-xl border border-ivory-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#d4853a]/10 text-[#d4853a] shrink-0">
                    <i className="ri-wallet-3-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-charcoal-300">Payment Terms</h3>
                  </div>
                </div>
                <p className="text-[14px] text-charcoal-50 leading-relaxed pl-[52px]">
                  {project.paymentPlan}
                </p>
              </div>
            )}
          </div>

          {/* Right: Sticky Inquiry Card */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-ivory-200">
              <h3 className="text-lg font-bold text-charcoal-300 mb-1">Interested in this project?</h3>
              <p className="text-[13px] text-charcoal-50 mb-6">
                Get in touch for detailed pricing, payment plans, and booking information.
              </p>

              <div className="flex flex-col gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-full py-3.5 bg-[#d4853a] text-white text-[13px] font-bold rounded-xl transition-all duration-300 hover:bg-[#c27630] hover:shadow-[0_8px_24px_rgba(212,133,58,0.3)] flex items-center justify-center gap-2.5"
                >
                  <i className="ri-whatsapp-line text-[18px]" />
                  WhatsApp Inquiry
                </a>
                <a
                  href={PHONE_URL}
                  className="w-full py-3.5 border border-charcoal-200 text-charcoal-300 text-[13px] font-semibold rounded-xl hover:bg-ivory-50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line text-[15px]" />
                  Call Now
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 border border-[#4a7c59] text-[#4a7c59] text-[13px] font-semibold rounded-xl hover:bg-[#4a7c59]/5 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <i className="ri-car-line text-[15px]" />
                  Book Site Visit
                </a>
                <button className="w-full py-3.5 border border-ivory-300 text-charcoal-100 text-[13px] font-medium rounded-xl hover:bg-ivory-50 transition-all duration-300 flex items-center justify-center gap-2">
                  <i className="ri-download-line text-[15px]" />
                  Download Brochure
                </button>
              </div>

              {/* Trust Badge */}
              <div className="mt-6 pt-5 border-t border-ivory-200 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#4a7c59]/10 shrink-0">
                  <i className="ri-shield-check-line text-[#4a7c59] text-[15px]" />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-charcoal-300">Verified &amp; NOC Approved</div>
                  <div className="text-[11px] text-charcoal-50">Complete legal documentation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}